import {useState, useEffect, useContext} from 'react';
import { PaperAirplaneIcon } from '@heroicons/react/24/solid';
import { AuthContext } from '../AuthContext';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import ManagerNav from './managernav';
import StaffNav from '../staff/staffnav';

export default function SicknessReport() {
    const [responseText, setResponseText] = useState('');
    const [messages, setMessages] = useState([]);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [currentQuestion, setCurrentQuestion] = useState("Why can't you attend work today? What are your symptoms?");
    const [questionCount, setQuestionCount] = useState(1);
    const [conversationFinished, setConversationFinished] = useState(false);
    const [previousQuestions, setPreviousQuestions] = useState([]);
    const [isClosing, setIsClosing] = useState(false);

    const user = useContext(AuthContext);
    const name = user.email;

    const navigate = useNavigate();

    // Initial system message
    const systemMessage = {
        Author: "System",
        Content: currentQuestion,
        CreatedAt: new Date().toISOString(),
        isSystem: true
    };

    // Function to handle sending messages
    const handleSendMessage = async () => {
        if (responseText.trim() === '' || conversationFinished) return;

        // Add user message immediately to UI
        const userMessage = {
            Author: name,
            Content: responseText,
            CreatedAt: new Date().toISOString(),
            isSystem: false
        };
        setMessages(prev => [...prev, userMessage]);

        // Store the current response and clear input
        const currentAnswer = responseText;
        setResponseText('');
        setIsSubmitted(true);

        try {
            const response = await fetch('https://api.insightos.co.uk/api/aisick', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    question: currentQuestion,
                    answer: currentAnswer,
                    questionCount: questionCount,
                    previousQuestions: previousQuestions
                }),
            });

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            const data = await response.json();
            console.log('Sickness report sent successfully:', data);

            // Process AI response after delay
            setTimeout(() => {
                // Check if we've reached 4 questions
                if (questionCount >= 6) {
                    setConversationFinished(true);
                    
                    // Add final thank you message
                    const finalMessage = {
                        Author: "AI Health Assistant",
                        Content: "Thank you for completing your sickness report. Based on your responses, please rest and get well soon! If your symptoms persist or worsen, please contact your GP. 🌟",
                        CreatedAt: new Date().toISOString(),
                        isSystem: true,
                        isAI: true
                    };
                    setMessages(prev => [...prev, finalMessage]);
                } else if (data.success && data.data && data.data.question && data.data.question.trim() !== '') {
                    // Continue conversation with new question from AI
                    setPreviousQuestions(prev => [...prev, currentQuestion]);
                    setCurrentQuestion(data.data.question);
                    setQuestionCount(prev => prev + 1);
                    
                    // Add the AI's new question as a system message
                    const aiQuestionMessage = {
                        Author: "AI Health Assistant",
                        Content: data.data.question,
                        CreatedAt: new Date().toISOString(),
                        isSystem: true,
                        isAI: true
                    };
                    setMessages(prev => [...prev, aiQuestionMessage]);
                    
                    // Re-enable input for next response
                    setIsSubmitted(false);
                } else {
                    // No more questions or error, end conversation
                    setConversationFinished(true);
                    
                    const finalMessage = {
                        Author: "AI Health Assistant",
                        Content: "Thank you for providing this information. Your sickness report has been completed. Please update us when your next scheduled in so we can assist you further. Please log in when your next shift is scheduled to update us on your situation. Get well soon! 🌟",
                        CreatedAt: new Date().toISOString(),
                        isSystem: true,
                        isAI: true
                    };
                    setMessages(prev => [...prev, finalMessage]);
                }
            }, 1500);

        } catch (error) {
            console.error('Error sending sickness report:', error);
            
            const errorMessage = {
                Author: "System",
                Content: "Sorry, there was an error submitting your report. Please try again or contact your manager directly.",
                CreatedAt: new Date().toISOString(),
                isSystem: true
            };
            setMessages(prev => [...prev, errorMessage]);
            
            // Re-enable input on error
            setIsSubmitted(false);
        }
    };

    // Function to close and send email
    const handleClose = async () => {
        if (isClosing) return;
        setIsClosing(true);

        try {
            const response = await fetch('https://api.insightos.co.uk/api/sickemail', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
            });
            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                throw new Error(data.message || 'Failed to send sick report');
            }
            console.log('Sick email sent successfully');
        } catch (error) {
            console.error('Error sending sick email:', error);
        } finally {
            navigate('/logout', { replace: true });
        }
    };

    // Initialize with system message
    useEffect(() => {
        setMessages([systemMessage]);
        // Initialize with the first question in previousQuestions for context
        setPreviousQuestions([]);
    }, []);

    return (
        <>
            {user.access === "manager" || user.access === "admin" ? (
                <ManagerNav />
            ) : null}
            {user.access === "staff" ? (
                <StaffNav />
            ) : null}
        
        <div className="w-full p-1">
            {/* Gradient background container */}
            <div className="relative rounded-3xl p-1 bg-gradient-to-br from-blue-800 via-purple-800 to-indigo-900 shadow-xl">
                {/* Glass effect inner box */}
                <div className="backdrop-blur-xl bg-black/20 border border-white/30 rounded-3xl p-4 md:p-6">
                    <h2 className="text-lg md:text-xl font-semibold text-white mb-2 md:mb-3 drop-shadow flex items-center justify-between">
                        <span>🤒 Sickness Report</span>
                        {!conversationFinished && (
                            <span className="text-sm font-normal bg-black/30 px-3 py-1 rounded-full text-blue-200">
                                Question {questionCount}/6
                            </span>
                        )}
                        {conversationFinished && (
                            <span className="text-sm font-normal bg-green-600/40 px-3 py-1 rounded-full text-green-200">
                                ✅ Complete
                            </span>
                        )}
                    </h2>
                    <div className="flex flex-col md:flex-row gap-3 md:gap-6">
                        {/* Messages - Moved to top on mobile */}
                        <div className="w-full md:flex-1 order-1 md:order-2">
                            <div className="p-3 md:p-4 bg-black/30 rounded-2xl md:rounded-3xl backdrop-blur-md h-48 md:h-64 overflow-y-auto border border-purple-500/30">
                                <ul className="space-y-3">
                                    {[...messages].reverse().map((message, index) => (
                                        <li key={messages.length - 1 - index} className={`text-sm ${message.isSystem ? 'text-blue-100' : 'text-white'}`}>
                                            <div className={`p-3 rounded-xl ${
                                                message.isSystem 
                                                    ? message.isAI 
                                                        ? 'bg-blue-600/30 border border-blue-400/50'
                                                        : 'bg-purple-600/30 border border-purple-400/50'
                                                    : 'bg-black/20 border border-white/30'
                                            }`}>
                                                <div className="flex flex-wrap items-center gap-1 mb-1">
                                                    <span className="font-semibold flex items-center">
                                                        {message.isSystem ? (message.isAI ? '� ' : '�🤖 ') : '👤 '}
                                                        {message.Author}:
                                                    </span>
                                                    <span className="text-xs text-white/70">
                                                        {formatDistanceToNow(new Date(message.CreatedAt), { addSuffix: true })}
                                                    </span>
                                                </div>
                                                <div className="mt-1">{message.Content}</div>
                                            </div>
                                        </li>
                                    ))}
                                    
                                    {/* Loading indicator for AI response */}
                                    {isSubmitted && !conversationFinished && (
                                        <li className="text-sm text-blue-100">
                                            <div className="p-3 rounded-xl bg-blue-600/30 border border-blue-400/50">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="font-semibold flex items-center">
                                                        🩺 AI Health Assistant:
                                                    </span>
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    <div className="flex space-x-1">
                                                        <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce"></div>
                                                        <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                                                        <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                                                    </div>
                                                    <span className="text-sm">Analyzing your symptoms...</span>
                                                </div>
                                            </div>
                                        </li>
                                    )}
                                </ul>
                            </div>
                        </div>
                        
                        {/* Textbox - Moved to bottom on mobile */}
                        <div className="w-full md:w-1/3 order-2 md:order-1 mt-3 md:mt-0">
                            <div className="relative">
                                <textarea
                                    onChange={e => setResponseText(e.target.value)}
                                    value={responseText}
                                    disabled={isSubmitted || conversationFinished}
                                    className={`w-full h-20 resize-none rounded-xl border border-purple-400/50 bg-black/30 p-3 pr-10 text-sm text-white placeholder-blue-200/70 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-purple-400/50 focus:border-transparent ${
                                        isSubmitted || conversationFinished ? 'opacity-50 cursor-not-allowed' : ''
                                    }`}
                                    placeholder={
                                        conversationFinished 
                                            ? "Assessment complete - no further input needed" 
                                            : isSubmitted 
                                                ? "Report submitted..." 
                                                : "Describe your symptoms and reason for absence..."
                                    }
                                />
                                <button
                                    onClick={handleSendMessage}
                                    disabled={isSubmitted || conversationFinished || responseText.trim() === ''}
                                    className={`absolute bottom-2 right-2 p-2 rounded-full shadow-md transition backdrop-blur-md ${
                                        isSubmitted || conversationFinished || responseText.trim() === ''
                                            ? 'bg-gray-500/20 cursor-not-allowed opacity-50'
                                            : 'bg-white/20 hover:bg-white/30'
                                    }`}
                                >
                                    <PaperAirplaneIcon className={`h-5 w-5 text-white ${
                                        !isSubmitted && !conversationFinished && responseText.trim() !== '' ? 'animate-pulse' : ''
                                    }`} />
                                </button>
                            </div>
                            
                            {!isSubmitted && !conversationFinished && (
                                <p className="text-xs text-white/70 mt-2 text-center">
                                    Please provide details about your symptoms and why you cannot attend work today.
                                </p>
                            )}
                            
                            {isSubmitted && !conversationFinished && (
                                <div className="mt-3 p-2 bg-blue-500/20 border border-blue-400/30 rounded-xl">
                                    <p className="text-xs text-blue-100 text-center">
                                        ⏳ Processing response...
                                    </p>
                                </div>
                            )}

                            {conversationFinished && (
                                <div className="mt-3 p-2 bg-green-500/20 border border-green-400/30 rounded-xl">
                                    <p className="text-xs text-green-100 text-center">
                                        ✅ Health assessment completed successfully
                                    </p>
                                    <button
                                        onClick={handleClose}
                                        disabled={isClosing}
                                        data-component="sickness-report"
                                        data-feature="close-and-send-report"
                                        data-time-saved="30"
                                        className="mt-2 w-full py-1 px-4 rounded-md font-semibold text-white bg-green-600 hover:bg-green-700 transition-all shadow-md disabled:cursor-wait disabled:opacity-60"
                                    >
                                        {isClosing ? 'Sending Report...' : 'Close & Send Report'}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
        </>
    );
}
